import { formatCurrency } from "@/components/Layout";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { formatDateLocal } from "@/lib/utils";
import { 
  TrendingUp,
  TrendingDown,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Calendar as CalendarIcon,
  X,
  Repeat,
  Plus,
  Pencil
} from "lucide-react";

const getFrequencyLabel = (freq) => {
  const labels = { 
    daily: 'Daily', 
    weekly: 'Weekly', 
    biweekly: 'Every 2 weeks', 
    fourweekly: 'Every 4 weeks',
    monthly: 'Monthly', 
    yearly: 'Yearly', 
    once: 'One-off' 
  };
  return labels[freq] || freq;
};

export const FullDayBreakdown = ({ 
  isOpen, 
  onClose, 
  dayData, 
  date, 
  currency, 
  onAddTransaction,
  onEditTransaction,
  onMarkPaid,
  isMobile,
  afterThisDay,
  isRunOutDate
}) => {
  if (!dayData || !date) return null;
  
  const { income, bills, debt, totalIn, totalOut, balance } = dayData;
  const hasTransactions = dayData.events.length > 0;
  
  const formattedDate = date.toLocaleDateString('en-GB', { 
    weekday: 'long',
    day: 'numeric', 
    month: 'long',
    year: 'numeric'
  });

  const handleItemClick = (item, type) => {
    onEditTransaction?.(item, type);
  };

  const handlePaidClick = (e, item, type) => {
    e.stopPropagation(); // Prevent opening edit sheet
    onMarkPaid?.(item, type, formatDateLocal(date));
  };

  const sheetContent = (
    <div className={`h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isMobile ? 'rounded-t-3xl' : ''} overflow-hidden`}>
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1.5 bg-white/30 rounded-full" />
        </div>
      )}
      
      {/* Header */}
      <div className="px-6 pt-4 pb-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">{formattedDate}</h2>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 text-white/60">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {/* Cash Flow Warning */}
        {isRunOutDate && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/20 border border-red-500/30 mb-5">
            <div className="w-8 h-8 rounded-lg bg-red-500/30 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-red-300">Projected Low Point</p>
              <p className="text-xs text-red-300/70">Your balance may go negative around this date</p>
            </div>
          </div>
        )}
        
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[10px] text-white/40 uppercase">Income</p>
            <p className="font-mono text-lg font-bold text-emerald-400">{formatCurrency(totalIn, currency)}</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[10px] text-white/40 uppercase">Bills</p>
            <p className="font-mono text-lg font-bold text-red-400">{formatCurrency(totalOut, currency)}</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-[10px] text-white/40 uppercase">Left for day</p>
            <p className={`font-mono text-lg font-bold ${balance >= 0 ? 'text-white' : 'text-red-400'}`}>
              {balance < 0 && '-'}{formatCurrency(Math.abs(balance), currency)}
            </p>
          </div>
        </div>
        
        {/* AFTER THIS DAY - Prominent */}
        {afterThisDay !== undefined && (
          <div className={`flex items-center justify-between px-5 py-4 rounded-2xl mb-6 ${
            afterThisDay < 0 ? 'bg-red-500/20 border border-red-500/30' :
            afterThisDay <= 100 ? 'bg-amber-500/15 border border-amber-500/30' :
            'bg-emerald-500/10 border border-emerald-500/20'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                afterThisDay < 0 ? 'bg-red-500/30' : afterThisDay <= 100 ? 'bg-amber-500/30' : 'bg-emerald-500/20'
              }`}>
                {afterThisDay < 0 ? (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                ) : afterThisDay <= 100 ? (
                  <AlertCircle className="w-5 h-5 text-amber-400" />
                ) : (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                )}
              </div>
              <div>
                <p className="text-sm font-bold text-white">After this day</p>
                <p className="text-xs text-white/50">
                  {afterThisDay < 0 ? 'You may be short' : afterThisDay <= 100 ? 'Balance is tight' : 'Looking good'}
                </p>
              </div>
            </div>
            <p className={`font-mono text-2xl font-bold ${
              afterThisDay < 0 ? 'text-red-400' : afterThisDay <= 100 ? 'text-amber-400' : 'text-emerald-400'
            }`}>
              {afterThisDay < 0 && '-'}{formatCurrency(Math.abs(afterThisDay), currency)}
            </p>
          </div>
        )}
        
        {/* Shortfall Explanation */}
        {afterThisDay !== undefined && afterThisDay < 0 && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6">
            <p className="text-sm text-red-300 font-semibold mb-2">Shortfall of {formatCurrency(Math.abs(afterThisDay), currency)}</p>
            <p className="text-xs text-white/60 mb-3">
              Based on your current balance and scheduled transactions, you may not have enough to cover all payments on this day.
            </p>
            <p className="text-xs text-white/50">
              <span className="font-semibold text-white/70">Suggested actions:</span>
              <br />• Move a non-essential bill to next week
              <br />• Add expected income that&apos;s coming in
              <br />• Top up your account before this date
            </p>
          </div>
        )}

        {hasTransactions ? (
          <div className="space-y-5">
            {/* Income Section */}
            {income.length > 0 && (
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Income</p>
                <div className="space-y-2">
                  {income.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => handleItemClick(item, 'income')}
                      className="w-full flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15 transition-colors text-left group"
                      data-testid={`edit-income-${item.income_id || i}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{item.name}</p>
                          {item.frequency && item.frequency !== 'once' && (
                            <p className="text-xs text-white/40 flex items-center gap-1">
                              <Repeat className="w-3 h-3" /> {getFrequencyLabel(item.frequency)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="font-mono text-lg font-semibold text-emerald-400">{formatCurrency(item.amount, currency)}</p>
                        <Pencil className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Bills Section */}
            {bills.length > 0 && (
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Bills / Expenses</p>
                <div className="space-y-2">
                  {bills.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 p-4 rounded-xl border transition-colors ${
                        item.is_paid 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-red-500/10 border-red-500/20'
                      }`}
                    >
                      {/* Mark as Paid Button */}
                      <button
                        onClick={(e) => handlePaidClick(e, item, 'bill')}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                          item.is_paid
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white/10 hover:bg-emerald-500/30 text-white/40 hover:text-emerald-400'
                        }`}
                        data-testid={`paid-btn-${item.bill_id || i}`}
                        title={item.is_paid ? 'Mark as unpaid' : 'Mark as paid'}
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      
                      {/* Item Content - Clickable for edit */}
                      <button
                        onClick={() => handleItemClick(item, 'bill')}
                        className={`flex-1 flex items-center justify-between text-left group ${
                          item.is_paid ? 'opacity-60' : ''
                        }`}
                        data-testid={`edit-bill-${item.bill_id || i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            item.is_paid ? 'bg-emerald-500/20' : 'bg-red-500/20'
                          }`}>
                            <TrendingDown className={`w-5 h-5 ${item.is_paid ? 'text-emerald-400' : 'text-red-400'}`} />
                          </div>
                          <div>
                            <p className={`font-medium text-white ${item.is_paid ? 'line-through' : ''}`}>{item.name}</p>
                            <div className="flex items-center gap-2">
                              {item.frequency && item.frequency !== 'once' && (
                                <p className="text-xs text-white/40 flex items-center gap-1">
                                  <Repeat className="w-3 h-3" /> {getFrequencyLabel(item.frequency)}
                                </p>
                              )}
                              {item.is_paid && (
                                <span className="text-xs text-emerald-400 font-medium">Paid</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className={`font-mono text-lg font-semibold ${
                            item.is_paid ? 'text-emerald-400' : 'text-red-400'
                          }`}>{formatCurrency(item.amount, currency)}</p>
                          <Pencil className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Debt Section */}
            {debt.length > 0 && (
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Debt Payments</p>
                <div className="space-y-2">
                  {debt.map((item, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 p-4 rounded-xl border transition-colors ${
                        item.is_paid 
                          ? 'bg-emerald-500/10 border-emerald-500/30' 
                          : 'bg-purple-500/10 border-purple-500/20'
                      }`}
                    >
                      {/* Mark as Paid Button */}
                      <button
                        onClick={(e) => handlePaidClick(e, item, 'debt')}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                          item.is_paid
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white/10 hover:bg-emerald-500/30 text-white/40 hover:text-emerald-400'
                        }`}
                        data-testid={`paid-btn-debt-${item.bill_id || i}`}
                        title={item.is_paid ? 'Mark as unpaid' : 'Mark as paid'}
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      
                      {/* Item Content - Clickable for edit */}
                      <button
                        onClick={() => handleItemClick(item, 'debt')}
                        className={`flex-1 flex items-center justify-between text-left group ${
                          item.is_paid ? 'opacity-60' : ''
                        }`}
                        data-testid={`edit-debt-${item.bill_id || i}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            item.is_paid ? 'bg-emerald-500/20' : 'bg-purple-500/20'
                          }`}>
                            <CreditCard className={`w-5 h-5 ${item.is_paid ? 'text-emerald-400' : 'text-purple-400'}`} />
                          </div>
                          <div>
                            <p className={`font-medium text-white ${item.is_paid ? 'line-through' : ''}`}>{item.name}</p>
                            <div className="flex items-center gap-2">
                              {item.frequency && item.frequency !== 'once' && (
                                <p className="text-xs text-white/40 flex items-center gap-1">
                                  <Repeat className="w-3 h-3" /> {getFrequencyLabel(item.frequency)}
                                </p>
                              )}
                              {item.is_paid && (
                                <span className="text-xs text-emerald-400 font-medium">Paid</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className={`font-mono text-lg font-semibold ${
                            item.is_paid ? 'text-emerald-400' : 'text-purple-400'
                          }`}>{formatCurrency(item.amount, currency)}</p>
                          <Pencil className="w-4 h-4 text-white/30 group-hover:text-white/60 transition-colors" />
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <CalendarIcon className="w-8 h-8 text-white/30" />
            </div>
            <p className="text-white/50 mb-2">No transactions</p>
            <p className="text-sm text-white/30">Add your first transaction for this day</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/10">
        <button 
          onClick={onAddTransaction}
          className="w-full py-4 bg-white text-slate-900 font-semibold rounded-xl flex items-center justify-center gap-2"
          data-testid="breakdown-add-btn"
        >
          <Plus className="w-5 h-5" /> Add transaction
        </button>
      </div>
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side={isMobile ? "bottom" : "right"} 
        hideCloseButton={true}
        className={`p-0 border-0 bg-transparent ${isMobile ? 'h-[90vh]' : 'w-[450px] sm:max-w-[450px]'}`}
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">Day Breakdown for {formattedDate}</SheetTitle>
        {sheetContent}
      </SheetContent>
    </Sheet>
  );
};

export default FullDayBreakdown;
